import React, { useMemo } from "react";
import {
  Stack,
  Group,
  Select,
  Button,
  Text,
  Paper,
  ActionIcon,
  Grid,
} from "@mantine/core";
import { IconPlus, IconTrash } from "@tabler/icons";
import McaConditionValueInput from "./McaConditionValueInput";
import {
  buildWatchFieldSelectOptions,
  getWatchFieldSelectOption,
} from "./mcaWatchFieldLabels";
import {
  emptyWatchFieldRow,
  watchFieldsToRows,
} from "../../helpers/mcaWatchFieldsHelpers";
import {
  getMcaWatchFieldMeta,
  getMcaConditionTypesForField,
  isMcaConditionTypeAllowed,
} from "../../helpers/mcaWatchFieldMeta";
import {
  MCA_ENTITIES,
  MCA_WATCH_FIELD_LOGIC,
  formatStepFieldLabel,
  isFileFieldType,
  isNumberFieldType,
} from "../../data/mcaEntities";
import { useMcaEditorReadOnly } from "./mcaEditorReadOnly";

export const STEP_CONDITION_PREFIX = "step:";

const FIELD_SELECT_STYLES = {
  dropdown: {
    minWidth: "max-content",
    width: "max-content",
    maxWidth: "min(90vw, 560px)",
  },
  item: {
    whiteSpace: "nowrap",
  },
};

const FIELD_SELECT_Z_INDEX = 5000;

const toEditorRow = (row) => {
  if (!row) return emptyWatchFieldRow();
  if (
    Object.prototype.hasOwnProperty.call(row, "conditionType") ||
    Object.prototype.hasOwnProperty.call(row, "conditionValue")
  ) {
    return {
      field: row.field ?? "",
      conditionType: row.conditionType ?? "",
      conditionValue: row.conditionValue ?? "",
    };
  }
  return watchFieldsToRows([row])[0] ?? emptyWatchFieldRow();
};

const stepFieldMeta = (inputType) => {
  if (isNumberFieldType(inputType)) {
    return { valueType: "number", options: null, refConfig: null };
  }
  if (inputType === "DATE" || inputType === "DATETIME") {
    return { valueType: "date", options: null, refConfig: null };
  }
  return { valueType: "text", options: null, refConfig: null };
};

const McaNotificationConditionsEditor = ({
  conditions = [],
  conditionLogic = "OR",
  onChange,
  entityKey = null,
  stepFields = [],
}) => {
  const readOnly = useMcaEditorReadOnly();
  const rows = useMemo(
    () => (conditions.length ? conditions.map(toEditorRow) : []),
    [conditions]
  );

  const entityPaths = useMemo(() => {
    const entity = MCA_ENTITIES.find((item) => item.value === entityKey);
    return entity?.watchFields ?? [];
  }, [entityKey]);

  const entityOptions = useMemo(
    () =>
      buildWatchFieldSelectOptions(entityKey, entityPaths).map((option) => ({
        ...option,
        group: "Entity",
      })),
    [entityKey, entityPaths]
  );

  const stepOptions = useMemo(
    () =>
      (stepFields ?? [])
        .filter((field) => field?.label?.trim() && !isFileFieldType(field.inputType))
        .map((field) => ({
          value: `${STEP_CONDITION_PREFIX}${field.label.trim()}`,
          label: formatStepFieldLabel(field),
          group: "This step",
          inputType: field.inputType,
        })),
    [stepFields]
  );

  const fieldOptions = useMemo(
    () =>
      [...entityOptions, ...stepOptions].map(({ value, label, group }) => ({
        value,
        label,
        group,
      })),
    [entityOptions, stepOptions]
  );

  const emit = (nextRows, nextLogic = conditionLogic) => {
    if (readOnly) return;
    onChange({
      conditionLogic: nextLogic === "AND" ? "AND" : "OR",
      conditions: nextRows.map((row) => ({
        field: row.field ?? "",
        conditionType: row.conditionType ?? "",
        conditionValue: row.conditionValue ?? "",
      })),
    });
  };

  const updateRow = (idx, patch) => {
    emit(rows.map((row, i) => (i === idx ? { ...row, ...patch } : row)));
  };

  const metaForRow = (row) => {
    const stepOption = stepOptions.find((option) => option.value === row.field);
    if (stepOption) return stepFieldMeta(stepOption.inputType);
    if (String(row.field ?? "").startsWith(STEP_CONDITION_PREFIX)) {
      return stepFieldMeta("TEXT");
    }
    return getMcaWatchFieldMeta(entityKey, row.field);
  };

  return (
    <Stack spacing="sm">
      <Text size="xs" color="dimmed">
        No conditions means this notification always sends when its trigger
        fires. Conditions on this step&apos;s input fields only match when the
        step is completed.
      </Text>
      {rows.length > 0 && fieldOptions.length === 0 && (
        <Text size="xs" color="dimmed">
          No fields to choose yet. Select a target entity on the Trigger tab, or
          add an input field on this step.
        </Text>
      )}
      {rows.length >= 2 && (
        <Select
          label="Condition logic"
          size="sm"
          data={MCA_WATCH_FIELD_LOGIC}
          value={conditionLogic === "AND" ? "AND" : "OR"}
          readOnly={readOnly}
          onChange={(value) => emit(rows, value ?? "OR")}
        />
      )}
      {rows.map((row, idx) => {
        const meta = metaForRow(row);
        const conditionTypes = getMcaConditionTypesForField(meta);
        const known = fieldOptions.some((option) => option.value === row.field);
        const extra =
          row.field && !known
            ? [
                String(row.field).startsWith(STEP_CONDITION_PREFIX)
                  ? {
                      value: row.field,
                      label: row.field.slice(STEP_CONDITION_PREFIX.length),
                      group: "This step",
                    }
                  : {
                      ...(getWatchFieldSelectOption(entityKey, row.field) ?? {
                        value: row.field,
                        label: row.field,
                      }),
                      group: "Entity",
                    },
              ]
            : [];
        return (
          <Paper key={idx} p="sm" withBorder>
            <Grid align="flex-end" gutter="sm">
              <Grid.Col span={12} md={5}>
                <Select
                  label="Field"
                  size="sm"
                  placeholder="Select field"
                  nothingFound="No matching fields"
                  data={[...fieldOptions, ...extra]}
                  value={row.field || null}
                  onChange={(value) =>
                    updateRow(idx, {
                      field: value ?? "",
                      conditionType: "",
                      conditionValue: "",
                    })
                  }
                  readOnly={readOnly}
                  searchable
                  clearable
                  withinPortal
                  zIndex={FIELD_SELECT_Z_INDEX}
                  maxDropdownHeight={320}
                  dropdownComponent="div"
                  styles={FIELD_SELECT_STYLES}
                />
              </Grid.Col>
              <Grid.Col span={6} md={3}>
                <Select
                  label="Condition"
                  size="sm"
                  data={conditionTypes}
                  value={row.conditionType || null}
                  onChange={(value) => {
                    const nextType = value ?? "";
                    if (nextType && !isMcaConditionTypeAllowed(meta, nextType)) return;
                    updateRow(idx, { conditionType: nextType, conditionValue: "" });
                  }}
                  readOnly={readOnly}
                  clearable
                  placeholder="Select"
                  withinPortal
                />
              </Grid.Col>
              <Grid.Col span={readOnly ? 6 : 4} md={readOnly ? 4 : 3}>
                <McaConditionValueInput
                  entityKey={entityKey}
                  field={row.field}
                  fieldMeta={meta}
                  conditionType={row.conditionType}
                  value={row.conditionValue}
                  onChange={(conditionValue) => updateRow(idx, { conditionValue })}
                  disabled={!row.conditionType}
                />
              </Grid.Col>
              {!readOnly && (
                <Grid.Col span={2} md={1}>
                  <Group position="right" pb={4}>
                    <ActionIcon
                      variant="subtle"
                      size="lg"
                      aria-label="Remove condition"
                      onClick={() => emit(rows.filter((_, i) => i !== idx))}
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Group>
                </Grid.Col>
              )}
            </Grid>
          </Paper>
        );
      })}
      {!readOnly && (
        <Button
          size="xs"
          variant="light"
          w="fit-content"
          leftIcon={<IconPlus size={14} />}
          onClick={() => emit([...rows, emptyWatchFieldRow()])}
        >
          Add condition
        </Button>
      )}
    </Stack>
  );
};

export default McaNotificationConditionsEditor;
